import { ThunkDispatch } from "redux-thunk";
import { IRootState } from "../store";
import {
  ICampaignActions,
  getCampaignListAction,
  getCampaignListSuccessAction,
  getCampaignListFailureAction,
  saveCampaignAction,
  saveCampaignFailureAction,
  saveCampaignSuccessAction,
  setCampaignListFiltersAction,
  getCampaignDetailsAction,
  getCampaignDetailsFailureAction,
  getCampaignDetailsSuccessAction,
  updateCampaignAction,
  updateCampaignFailureAction,
  updateCampaignSuccessAction,
  updateCampaignStatusAction,
  updateCampaignStatusFailureAction,
  updateCampaignStatusSuccessAction,
  getTrackierFailureAction,
  getTrackierSuccessAction,
  getTrackierAction,
  saveTrackierAction,
  saveTrackierFailureAction,
  saveTrackierSuccessAction,
  getAggregateReportAction,
  getAggregateReportFailureAction,
  getAggregateReportSuccessAction,
} from "../actions/campaign.action";
import {
  ICampaignDTO,
  ICampaignFiltersDto,
} from "../../interfaces/dto/campaign.dto";
import {
  getCampaigns,
  saveCampaign,
  getCampaignDetails,
  updateCampaignDetails,
  updateCampaignStatus,
  getTrackier,
  saveTrackier,
  getAggregateReport,
} from "../../services/campaigns";
import {
  getIosVersionThunk,
  getAgeGroupThunk,
  getAndroidVersionThunk,
  getInterestsThunk,
  getLanguageThunk,
  getCountriesThunk,
} from "./global.thunk";
import { sessionCheck } from "./auth.thunk";
import { CampaignStatus } from "../../enum/campaign";
import { getUsersThunk } from "./user.thunk";
import { Role } from "../../enum/auth";
import { IDateRange } from "../../interfaces/campaign";

export const setCampaignListFiltersThunk = (filters: ICampaignFiltersDto) => {
  return (dispatch: ThunkDispatch<IRootState, void, ICampaignActions>) => {
    dispatch(setCampaignListFiltersAction(filters));
    dispatch(getCampaignThunk());
  };
};

export const getCampaignThunk =
  () =>
  async (
    dispatch: ThunkDispatch<IRootState, void, ICampaignActions>,
    getState: () => IRootState,
  ) => {
    const { auth, campaign } = getState();

    if (auth.userId && auth.token) {
      dispatch(getCampaignListAction());

      const {
        filters = {
          offset: 0,
          limit: 10,
        },
      } = campaign.list;

      const campaigns = await getCampaigns(
        { ...filters, userId: auth.userId },
        auth.token ?? "",
      );

      if (campaigns.error && dispatch(sessionCheck(campaigns))) {
        dispatch(getCampaignListFailureAction(campaigns.error));
      } else {
        dispatch(getCampaignListSuccessAction(campaigns));
      }
    }
  };

export const saveCampaignThunk =
  (
    payload: ICampaignDTO,
    campaignId?: string,
    files?: Array<File>,
    userId?: string,
  ) =>
  async (
    dispatch: ThunkDispatch<IRootState, void, ICampaignActions>,
    getState: () => IRootState,
  ) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      if (campaignId) {
        dispatch(updateCampaignAction());

        const data = await updateCampaignDetails(
          userId ?? auth.userId,
          campaignId,
          payload,
          auth.token,
          files,
        );

        if (data.error && dispatch(sessionCheck(data))) {
          dispatch(updateCampaignFailureAction(data.error));
        } else {
          dispatch(updateCampaignSuccessAction(data));

          return true;
        }
      } else {
        dispatch(saveCampaignAction());

        const data = await saveCampaign(
          payload,
          auth.token,
          userId ?? auth.userId,
          files,
        );

        if (data.error && dispatch(sessionCheck(data))) {
          dispatch(saveCampaignFailureAction(data.error));
        } else {
          dispatch(saveCampaignSuccessAction(data));

          return true;
        }
      }
    }

    return false;
  };

export const updateCampaignStatusThunk =
  (campaignId: string, status: CampaignStatus) =>
  async (
    dispatch: ThunkDispatch<IRootState, void, ICampaignActions>,
    getState: () => IRootState,
  ) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      dispatch(updateCampaignStatusAction());

      const data = await updateCampaignStatus(
        campaignId,
        auth.userId,
        status,
        auth.token,
      );

      if (data.error && dispatch(sessionCheck(data))) {
        dispatch(updateCampaignStatusFailureAction(data.error));
      } else {
        dispatch(updateCampaignStatusSuccessAction(data));
        return true;
      }
    }

    return false;
  };

export const getCampaignDetailsThunk =
  (campaignId: string) =>
  async (
    dispatch: ThunkDispatch<IRootState, void, ICampaignActions>,
    getState: () => IRootState,
  ) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      dispatch(getCampaignDetailsAction());

      const data = await getCampaignDetails(
        auth.userId,
        campaignId,
        auth.token ?? "",
      );

      if (data.error && dispatch(sessionCheck(data))) {
        dispatch(getCampaignDetailsFailureAction(data.error));
      } else {
        dispatch(getCampaignDetailsSuccessAction(data));
      }
    }
  };

export const getTrackierThunk = (campaignId: string) => 
  async (dispatch: ThunkDispatch<IRootState, void, ICampaignActions>, getState: () => IRootState) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      dispatch(getTrackierAction());
      const trackier = await getTrackier(auth.userId, campaignId, auth.token);

      if(trackier.error && dispatch(sessionCheck(trackier))) {
        dispatch(getTrackierFailureAction(trackier.error));
      } else {
        dispatch(getTrackierSuccessAction(trackier.data));
      }
    }
  }

export const saveTrackierThunk = (campaignId: string, trackierIds: Array<string>) =>
  async (dispatch: ThunkDispatch<IRootState, void, ICampaignActions>, getState: () => IRootState) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      dispatch(saveTrackierAction());
      const trackier = await saveTrackier(auth.userId, campaignId, trackierIds, auth.token);

      if(trackier.error && dispatch(sessionCheck(trackier))) {
        dispatch(saveTrackierFailureAction(trackier.error));
      } else {
        dispatch(saveTrackierSuccessAction(trackier.result));
      }
    }
  }

export const getAggregateReportThunk = (dateRange: IDateRange) =>
  async (dispatch: ThunkDispatch<IRootState, void, ICampaignActions>, getState: () => IRootState) => {
    const { auth } = getState();

    if (auth.userId && auth.token) {
      dispatch(getAggregateReportAction());
      const aggregateReport = await getAggregateReport(auth.userId, auth.token, dateRange);

      if(aggregateReport.error && dispatch(sessionCheck(aggregateReport))) {
        dispatch(getAggregateReportFailureAction(aggregateReport.error));
      } else {
        dispatch(getAggregateReportSuccessAction(aggregateReport));
      }
    }
  
  }

export const initCampaignThunk =
  () =>
  async (
    dispatch: ThunkDispatch<IRootState, void, ICampaignActions>,
    getState: () => IRootState,
  ) => {
    const { auth } = getState();

    if (auth.token) {
      if (auth.role === Role.ADMIN) {
        dispatch(getUsersThunk());
      }

      dispatch(getIosVersionThunk());
      dispatch(getAndroidVersionThunk());
      dispatch(getInterestsThunk());
      dispatch(getAgeGroupThunk());
      dispatch(getLanguageThunk());
      dispatch(getCountriesThunk());
    }
  };
